import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const NotificationSettings = ({ user, onUpdate }) => {
  const [isSaving, setIsSaving] = useState(false);

  // Notification preferences grouped by category
  const [notificationPreferences, setNotificationPreferences] = useState({
    // Account & Security
    security: {
      title: 'Sicherheit & Konto',
      description: 'Wichtige Sicherheitswarnungen und Konto-Updates',
      icon: 'Shield',
      settings: {
        loginAlerts: {
          label: 'Anmeldungen von neuen Geräten',
          description: 'Benachrichtigung bei Anmeldungen von unbekannten Geräten',
          email: true,
          push: true,
          sms: false
        },
        passwordChanges: {
          label: 'Passwort-Änderungen',
          description: 'Bestätigung bei Passwort-Änderungen',
          email: true,
          push: false,
          sms: false
        },
        accountChanges: {
          label: 'Konto-Änderungen',
          description: 'Updates bei wichtigen Kontoeinstellungen',
          email: true,
          push: false,
          sms: false
        }
      }
    },

    // AI Agents
    agents: {
      title: 'AI-Agents',
      description: 'Updates und Aktivitäten Ihrer AI-Agents',
      icon: 'Bot',
      settings: {
        agentErrors: {
          label: 'Agent-Fehler',
          description: 'Benachrichtigungen bei Agent-Ausfällen oder Fehlern',
          email: true,
          push: true,
          sms: false
        },
        agentUpdates: {
          label: 'Agent-Updates',
          description: 'Informationen über neue Features und Verbesserungen',
          email: false,
          push: true,
          sms: false
        },
        usageReports: {
          label: 'Nutzungsberichte',
          description: 'Wöchentliche Berichte über Agent-Nutzung',
          email: true,
          push: false,
          sms: false
        }
      }
    },

    // Billing & Limits
    billing: {
      title: 'Abrechnung & Limits',
      description: 'Rechnungen, Zahlungen und Nutzungslimits',
      icon: 'CreditCard',
      settings: {
        billingAlerts: {
          label: 'Rechnungsbenachrichtigungen',
          description: 'Neue Rechnungen und Zahlungsbestätigungen',
          email: true,
          push: false,
          sms: false
        },
        usageLimits: {
          label: 'Nutzungslimits',
          description: 'Warnung bei Erreichen von 80% der Limits',
          email: true,
          push: true,
          sms: true
        },
        paymentIssues: {
          label: 'Zahlungsprobleme',
          description: 'Fehlgeschlagene Zahlungen und Mahnungen',
          email: true,
          push: false,
          sms: true
        }
      }
    },

    // Product & Marketing
    marketing: {
      title: 'Produkt & Marketing',
      description: 'Neue Features, Ankündigungen und Marketing-Inhalte',
      icon: 'Megaphone',
      settings: {
        productUpdates: {
          label: 'Produkt-Updates',
          description: 'Neue Features und Verbesserungen',
          email: false,
          push: false,
          sms: false
        },
        newsletters: {
          label: 'Newsletter',
          description: 'Monatlicher Newsletter mit Tipps und Tricks',
          email: false,
          push: false,
          sms: false
        },
        promotions: {
          label: 'Werbeaktionen',
          description: 'Rabatte und spezielle Angebote',
          email: false,
          push: false,
          sms: false
        }
      }
    }
  });

  const handleToggle = (categoryKey, settingKey, channelKey) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev?.[categoryKey],
        settings: {
          ...prev?.[categoryKey]?.settings,
          [settingKey]: {
            ...prev?.[categoryKey]?.settings?.[settingKey],
            [channelKey]: !prev?.[categoryKey]?.settings?.[settingKey]?.[channelKey]
          }
        }
      }
    }));
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In real app, this would save to backend
      onUpdate?.({ notificationPreferences });
      
      // Show success feedback
      alert('Benachrichtigungseinstellungen gespeichert');
    } catch (error) {
      console.error('Failed to save notifications:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getToggleButton = (isEnabled, categoryKey, settingKey, channelKey) => (
    <button
      onClick={() => handleToggle(categoryKey, settingKey, channelKey)}
      className={cn(
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
        isEnabled ? "bg-primary" : "bg-muted"
      )}
    >
      <span
        className={cn(
          "inline-block h-3 w-3 transform rounded-full bg-background transition-transform",
          isEnabled ? "translate-x-5" : "translate-x-1"
        )}
      />
    </button>
  );

  const channels = [
    { key: 'email', label: 'E-Mail', icon: 'Mail' },
    { key: 'push', label: 'Push', icon: 'Bell' },
    { key: 'sms', label: 'SMS', icon: 'MessageSquare' }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Benachrichtigungseinstellungen</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Steuern Sie, wie und wann Sie Benachrichtigungen erhalten
          </p>
        </div>
        
        <Button
          onClick={handleSaveAll}
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
        >
          Alle speichern
        </Button>
      </div>

      {/* Notification Categories */}
      {Object.entries(notificationPreferences)?.map(([categoryKey, category]) => (
        <div key={categoryKey} className="bg-card rounded-lg border border-border p-6">
          {/* Category Header */}
          <div className="flex items-start gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={category?.icon} size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-foreground">{category?.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{category?.description}</p>
            </div>
          </div>

          {/* Channel Headers */}
          <div className="hidden md:flex items-center justify-end gap-12 mb-4 pr-4">
            {channels?.map((channel) => (
              <div key={channel?.key} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name={channel?.icon} size={14} />
                <span>{channel?.label}</span>
              </div>
            ))}
          </div>

          {/* Settings */}
          <div className="space-y-6">
            {Object.entries(category?.settings)?.map(([settingKey, setting]) => (
              <div key={settingKey} className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-3 border-b border-border last:border-b-0">
                {/* Setting Info */}
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground">{setting?.label}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{setting?.description}</p>
                </div>

                {/* Channel Controls */}
                <div className="flex items-center gap-8 md:gap-12">
                  {channels?.map((channel) => (
                    <div key={channel?.key} className="flex flex-col items-center gap-2">
                      {/* Mobile Channel Label */}
                      <div className="md:hidden flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name={channel?.icon} size={12} />
                        <span>{channel?.label}</span>
                      </div>
                      
                      {/* Toggle */}
                      {getToggleButton(
                        setting?.[channel?.key], 
                        categoryKey, 
                        settingKey, 
                        channel?.key
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Delivery Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Settings" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground">Zustellungseinstellungen</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Konfigurieren Sie, wie Benachrichtigungen zugestellt werden
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Icon name="Mail" size={16} />
              E-Mail-Einstellungen
            </h4>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Digest-Modus</span>
                <select className="bg-background border border-input rounded-md px-2 py-1 text-xs">
                  <option value="immediate">Sofort</option>
                  <option value="hourly">Stündlich</option>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                </select>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Ruhemodus</span>
                <div className="text-xs">22:00 - 08:00</div>
              </div>
            </div>
          </div>

          {/* Push Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Icon name="Bell" size={16} />
              Push-Einstellungen
            </h4>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Browser-Benachrichtigungen</span>
                <button
                  onClick={() => {
                    if ('Notification' in window) {
                      Notification.requestPermission();
                    }
                  }}
                  className="text-primary text-xs hover:underline"
                >
                  Aktivieren
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Ruhemodus</span>
                <div className="text-xs">22:00 - 08:00</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Test Notification */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="TestTube" size={16} className="text-warning" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-foreground">Benachrichtigungen testen</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Senden Sie eine Test-Benachrichtigung, um Ihre Einstellungen zu überprüfen
            </p>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={() => alert('Test-E-Mail versendet!')}
              >
                E-Mail testen
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Bell"
                onClick={() => {
                  if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('Test-Benachrichtigung', {
                      body: 'Dies ist eine Test-Push-Benachrichtigung von Vierdimensional.',
                      icon: '/favicon.ico'
                    });
                  } else {
                    alert('Push-Benachrichtigungen sind nicht aktiviert');
                  }
                }}
              >
                Push testen
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="MessageSquare"
                onClick={() => alert('Test-SMS würde versendet (Demo-Modus)')}
              >
                SMS testen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;